/**
 * Sanitization + validation helpers for webhook configuration text inputs
 * (webhook URL and header key/value pairs).
 *
 * Users almost always copy-paste these values from Slack, docs, email, etc.,
 * which silently drags in invisible or non-typeable characters (zero-width
 * spaces, BOMs, non-breaking spaces, bidi controls, stray CR/LF). Those
 * characters pass the UI unnoticed and later break outbound HTTP header
 * construction on the backend with no readable log.
 *
 * Strategy is two-layered:
 *  - `stripInvisibleChars` / `sanitizeConfigInput` silently remove or
 *    normalize what is always safe to fix (invisible chars, control chars,
 *    exotic whitespace). `stripInvisibleChars` keeps surrounding spaces so it
 *    is safe to run on every keystroke; `sanitizeConfigInput` also trims and
 *    is meant for blur/submit.
 *  - `validateHeaderKey` / `validateHeaderValue` surface an error for what we
 *    must NOT silently rewrite (a key that isn't a valid HTTP token, a value
 *    with unsupported non-ASCII characters), so the user can correct it.
 */

// Zero-width space (U+200B), ZWNJ (U+200C), ZWJ (U+200D), word joiner (U+2060),
// BOM / zero-width no-break space (U+FEFF). Never legitimate here.
const ZERO_WIDTH_CHARS = /[\u200B-\u200D\u2060\uFEFF]/g;

// Bidirectional formatting controls: LRE/RLE/PDF/LRO/RLO (U+202A-U+202E) and
// the isolate controls LRI/RLI/FSI/PDI (U+2066-U+2069).
const BIDI_CONTROL_CHARS = /[\u202A-\u202E\u2066-\u2069]/g;

// C0 controls + DEL + C1 controls. This intentionally also strips CR/LF/TAB,
// which doubles as a guard against header (CRLF) injection.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;

// Whitespace that isn't a plain ASCII space: NBSP (U+00A0), ogham space
// (U+1680), en/em & related spaces (U+2000-U+200A), line/para separators
// (U+2028/U+2029), narrow/medium math spaces (U+202F, U+205F), ideographic
// space (U+3000). Normalized to a normal space.
const UNICODE_WHITESPACE = /[\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g;

// RFC 7230 header field-name token: visible ASCII minus separators.
const HEADER_KEY_TOKEN = /^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/;

// Printable ASCII (space through tilde). What an HTTP header value may contain.
// eslint-disable-next-line no-control-regex
const PRINTABLE_ASCII = /^[\x20-\x7E]*$/;

/**
 * Remove invisible/control characters and normalize exotic whitespace to a
 * plain space, WITHOUT trimming. Safe to run on every keystroke: it never
 * eats the leading/trailing spaces a user may still be typing. A no-op for
 * clean ASCII.
 */
export function stripInvisibleChars(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }
  return raw
    .normalize('NFC')
    .replace(ZERO_WIDTH_CHARS, '')
    .replace(BIDI_CONTROL_CHARS, '')
    .replace(CONTROL_CHARS, '')
    .replace(UNICODE_WHITESPACE, ' ');
}

/**
 * Full normalization for blur/submit: everything `stripInvisibleChars` does,
 * plus trimming surrounding whitespace. A no-op for clean, trimmed ASCII, so
 * existing valid values round-trip unchanged.
 */
export function sanitizeConfigInput(raw: string | null | undefined): string {
  return stripInvisibleChars(raw).trim();
}

/** Sanitize a header key. Alias of {@link sanitizeConfigInput} for call-site clarity. */
export const sanitizeHeaderKey = (raw: string | null | undefined): string => sanitizeConfigInput(raw);

/** Sanitize a header value. Alias of {@link sanitizeConfigInput} for call-site clarity. */
export const sanitizeHeaderValue = (raw: string | null | undefined): string => sanitizeConfigInput(raw);

/**
 * Validate a (already sanitized) header key. Returns '' when valid, otherwise a
 * user-facing error message. An empty key is treated as valid here because
 * empty header rows are filtered out before submission.
 */
export function validateHeaderKey(key: string): string {
  if (!key) {
    return '';
  }
  if (!HEADER_KEY_TOKEN.test(key)) {
    return "Header Key can only contain letters, numbers, and ! # $ % & ' * + - . ^ _ ` | ~";
  }
  return '';
}

/**
 * Validate a (already sanitized) header value. Returns '' when valid, otherwise
 * a user-facing error message. Anything outside printable ASCII survived
 * sanitization intentionally (e.g. emoji or accented letters) and must be
 * corrected rather than silently dropped.
 */
export function validateHeaderValue(value: string): string {
  if (!PRINTABLE_ASCII.test(value)) {
    return 'Header value contains unsupported characters. Use standard letters, numbers, and symbols only.';
  }
  return '';
}

/**
 * Validate a header key/value pair as a unit, returning the error message for
 * each field ('' when valid).
 *
 * A header is a key + value pair, so a row that has been started (one field
 * filled) must have both filled: a value with no key, or a key with no value,
 * yields a "required" error on the missing field. A fully-empty row (neither
 * field filled) is treated as unused and reports no error. Format errors from
 * {@link validateHeaderKey} / {@link validateHeaderValue} take precedence.
 */
export function validateHeaderPair(key: string, value: string): { key: string; value: string } {
  const hasKey = key.trim().length > 0;
  const hasValue = value.trim().length > 0;

  let keyError = '';
  if (hasKey) {
    keyError = validateHeaderKey(key);
  } else if (hasValue) {
    keyError = 'Key is required';
  }

  let valueError = '';
  if (hasValue) {
    valueError = validateHeaderValue(value);
  } else if (hasKey) {
    valueError = 'Value is required';
  }

  return { key: keyError, value: valueError };
}
